import React from 'react';
import { motion } from 'framer-motion';
import { Plane } from 'lucide-react';
import ImageCarousel from './ImageCarousel';

const MAP_IMAGES = [
  "https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/r4q781v0_carte%202.jpg",
  "https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/veeuf8vb_carte.jpg",
  "https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/8947tle3_Carte%203.jpg"
];

const MAP_SCALES = [1, 1, 0.6];

const INTERNATIONAL_FLIGHTS = [
  ['Paris', '6hr 10mn'],
  ['Lisbon', '4hr 20mn'],
  ['Bruxelles', '7hr 10mn'],
  ['Amsterdam', '7hr 05mn'],
  ['Luxembourg', '6hr 35mn'],
];

const NATIONAL_FLIGHTS = [
  ['Santiago', '55mn'],
  ['Sal', '45mn'],
  ['São Nicolau', '40mn'],
];

const FlightColumn = ({ title, flights, delay, fromLeft }) => (
  <motion.div
    initial={{ opacity: 0, x: fromLeft ? -20 : 20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6, delay }}
  >
    <h3 className="text-black font-medium mb-6 uppercase tracking-[0.15em] text-sm border-b border-black/20 pb-2">
      {title}
    </h3>
    <div className="space-y-4">
      {flights.map(([city, duration]) => (
        <div key={city} className="flex justify-between text-base">
          <span className="text-black/70">{city}</span>
          <span className="text-black italic">{duration}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const MapsCarousel = () => (
  <ImageCarousel 
    images={MAP_IMAGES}
    alt="Cape Verde Maps"
    className="aspect-[4/3]"
    objectFit="contain"
    showArrows={true}
    scales={MAP_SCALES}
  />
);

const TravelInfoSections = () => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.6 }}
    className="mt-16 pb-16"
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
      {/* E-VOA Section */}
      <div>
        <h2 className="font-serif text-2xl md:text-3xl text-black mb-6 text-center lg:text-left">
          E-VOA (Electronic Visa on Arrival)
        </h2>
        <p className="text-black/70 font-light leading-relaxed text-center lg:text-left">
          The Cape Verdean Immigration Agency has launched an online pre-arrival application, EASE (Efficient Automatic and Safe Entry of Travelers). The application is to be completed via a government portal operated by the Cape Verdean Immigration authorities to obtain an e-VOA - a single-entry visa that is valid for stays of up to 30 days with the purpose of tourism, government visits, business meetings, goods purchasing, or transit only. To apply for the e-VOA and to check the countries eligible, please{' '}
          <a 
            href="https://www.ease.gov.cv" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-black underline hover:no-underline transition-all duration-300"
          >
            click here
          </a>.
        </p>
      </div>

      {/* Airport Transfers Section */}
      <div>
        <h2 className="font-serif text-2xl md:text-3xl text-black mb-6 text-center lg:text-left">
          Airport transfers
        </h2>
        <p className="text-black/70 font-light leading-relaxed text-center lg:text-left">
          Respiro is a 20-minute drive from São Vicente's São Pedro International Airport (VXE). Respiro's dedicated concierge team can arrange transfers upon request.
        </p>
      </div>
    </div>
  </motion.div>
);

const GettingHere = () => {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="getting-here-section">
      <div className="container mx-auto px-6 md:px-12 max-w-[1400px]">
        {/* Flight Duration Section with Map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Flight Info - Left Side */}
            <div>
              <h1 className="font-serif text-3xl lg:text-4xl text-black mb-8 text-center lg:text-left">
                Getting to Respiro
              </h1>
              
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                <Plane size={24} className="text-black" />
                <h2 className="font-serif text-2xl md:text-3xl text-black">
                  Flight duration to São Vicente
                </h2>
              </div>
              <p className="text-black/70 font-light text-center lg:text-left mb-12">
                Direct flights into São Vicente's São Pedro International Airport
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-xl">
                <FlightColumn title="International" flights={INTERNATIONAL_FLIGHTS} delay={0.4} fromLeft />
                <FlightColumn title="National" flights={NATIONAL_FLIGHTS} delay={0.5} />
              </div>
            </div>

            {/* Maps Carousel - Right Side (Desktop) */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <MapsCarousel />
            </motion.div>
          </div>

          {/* Maps Carousel - Mobile */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 lg:hidden max-w-[500px] mx-auto"
          >
            <MapsCarousel />
          </motion.div>
        </motion.div>

        <TravelInfoSections />

        {/* Ribbon Image */}
        <div className="w-full mt-12 mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_respiro-retreat/artifacts/e29mi0fz_Barefoot3062.jpg"
            alt="São Vicente panoramic view"
            loading="lazy"
            className="w-full h-[300px] md:h-[400px] object-cover"
          />
        </div>
      </div>
    </section>
  );
};

export default GettingHere;
